import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { StoreService } from '../services/StoreService';
import { IStoreStatus } from '../types/OpeningHours';
import { Environment } from '../environment';

interface StatusContextData {
  status: IStoreStatus | null;
  isLoading: boolean;
  refreshStatus: () => Promise<void>;
  isOpen: boolean;
  message: string;
  isGestorMode: boolean;
}

const StatusContext = createContext<StatusContextData>({} as StatusContextData);

export const useStatus = () => useContext(StatusContext);

export const StatusProvider: React.FC<{ storeId: string; children: React.ReactNode }> = ({ storeId, children }) => {
  const [status, setStatus] = useState<IStoreStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isGestorMode, setIsGestorMode] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('mode') === 'gestor' && !!params.get('token');
  });

  const refreshStatus = useCallback(async () => {
    if (!storeId) return;

    try {
      const statusData = await StoreService.getStatus(storeId);
      setStatus(statusData);
    } catch (_) {
    } finally {
      setIsLoading(false);
    }
  }, [storeId]);

  // Carregar status inicial
  useEffect(() => {
    refreshStatus();
  }, [refreshStatus]);

  // Atualizar status a cada minuto
  useEffect(() => {
    const interval = setInterval(() => {
      refreshStatus();
    }, 60000); // 1 minuto

    return () => clearInterval(interval);
  }, [refreshStatus]);

  // Confirmar token do modo gestor — reverte se inválido
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('mode') !== 'gestor') return;
    const token = params.get('token');
    if (!token) return;
    fetch(`${Environment.API_URL}/store/validate-gestor-token?token=${encodeURIComponent(token)}`, {
      headers: { 'x-api-key': Environment.API_KEY },
    })
      .then(r => r.json())
      .then(data => { if (!data.valid) setIsGestorMode(false); })
      .catch(() => { setIsGestorMode(false); });
  }, []);

  const isOpen = status?.isOpen ?? false;
  const message = status?.message ?? '';

  return (
    <StatusContext.Provider value={{ status, isLoading, refreshStatus, isOpen, message, isGestorMode }}>
      {children}
    </StatusContext.Provider>
  );
};
